import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { agentAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { Search, ChevronRight, Shield, Mail, Bug, KeyRound, Skull, Network, Eye, Lock, Globe, Code, Cloud, ClipboardCheck, Zap, GraduationCap, FileText, ShieldAlert, Siren } from 'lucide-react';
import { motion } from 'framer-motion';

const ICON_MAP = {
  Shield, Mail, Bug, KeyRound, Skull, Network, Eye, Lock, Globe, Code, Cloud,
  ClipboardCheck, Zap, GraduationCap, FileText, ShieldAlert, Siren, Search,
};

const TIER_COLORS = {
  flagship: { bg: 'bg-[#FF7A3D]/10', text: 'text-[#FF7A3D]', border: 'border-[#FF7A3D]/20' },
  standard: { bg: 'bg-[#00E5C1]/10', text: 'text-[#00E5C1]', border: 'border-[#00E5C1]/20' },
  premium: { bg: 'bg-[#ffbd2e]/10', text: 'text-[#ffbd2e]', border: 'border-[#ffbd2e]/20' },
};

export default function Marketplace() {
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState({});
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    agentAPI.list().then(res => {
      setAgents(res.data.agents);
      setCategories(res.data.categories);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = agents.filter(a => {
    const matchesFilter = filter === 'all' || a.category === filter;
    const matchesSearch = !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.short_desc.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const uniqueCategories = [...new Set(agents.map(a => a.category))];

  return (
    <div className="min-h-screen bg-[#020202] grid-bg" data-testid="marketplace-page">
      <Navbar />
      <div className="pt-20 px-6 md:px-12 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <p className="font-mono text-xs text-[#FF7A3D] tracking-widest uppercase mb-2">AGENT MARKETPLACE</p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
              18 Agents. One Mission.
            </h1>
            <p className="font-body text-lg text-[#999] mt-2">Select and deploy specialized AI security agents</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#aaa]" />
              <Input
                placeholder="Search agents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-black border-white/10 focus:border-[#FF7A3D] font-mono text-sm placeholder:text-[#555]"
                data-testid="search-agents-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilter('all')}
                className={`font-mono text-xs px-3 py-1.5 rounded-sm border transition-all ${filter === 'all' ? 'border-[#FF7A3D]/50 bg-[#FF7A3D]/10 text-[#FF7A3D]' : 'border-white/10 text-[#999] hover:border-white/20'}`}
                data-testid="filter-all"
              >
                ALL ({agents.length})
              </button>
              {uniqueCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`font-mono text-xs px-3 py-1.5 rounded-sm border transition-all ${filter === cat ? 'border-[#FF7A3D]/50 bg-[#FF7A3D]/10 text-[#FF7A3D]' : 'border-white/10 text-[#999] hover:border-white/20'}`}
                  data-testid={`filter-${cat}`}
                >
                  {categories[cat]?.name || cat.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Agent Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="agents-grid">
              {filtered.map((agent, i) => {
                const IconComp = ICON_MAP[agent.icon] || Shield;
                const tierStyle = TIER_COLORS[agent.tier] || TIER_COLORS.standard;
                return (
                  <motion.div
                    key={agent.agent_id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <div
                      className="card-cyber p-5 rounded-sm h-full flex flex-col cursor-pointer group"
                      onClick={() => navigate(`/agents/${agent.agent_id}`)}
                      data-testid={`agent-card-${agent.agent_id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-white/[0.03] border border-white/5 group-hover:border-[#FF7A3D]/30 transition-colors">
                          <IconComp className="w-5 h-5 text-[#FF7A3D]" />
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${tierStyle.bg} ${tierStyle.text} ${tierStyle.border} font-mono text-[10px] uppercase`}>
                            {agent.tier}
                          </Badge>
                        </div>
                      </div>
                      <h3 className="font-heading text-sm font-bold text-white mb-1.5 group-hover:text-[#FF7A3D] transition-colors">{agent.name}</h3>
                      <p className="font-body text-xs text-[#aaa] leading-relaxed flex-1">{agent.short_desc}</p>
                      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                        {agent.is_active ? (
                          <span className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF7A3D]" />
                            <span className="font-mono text-[10px] text-[#FF7A3D]">ACTIVE</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#555]" />
                            <span className="font-mono text-[10px] text-[#aaa]">COMING SOON</span>
                          </span>
                        )}
                        <span className="font-mono text-[10px] text-[#888]">{agent.price_per_run} cr/run</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
